import {token} from "@atlaskit/tokens";
import type {EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import {
    EMOTORAD__LEAD__CREATE__FORM__ID,
} from "~/domainModels/workspaceSpecific/emotorad_old";
import {
    EMOTORAD__SALES__SCHEDULE__FORM__ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {
    ResolvedCalendarScheduleEmptyState,
    SharedCalendarColorConfig,
    SharedCalendarLegendItem,
    SharedCalendarScheduleEmptyStateConfig,
    SharedCalendarViewConfig,
} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {resolveDesignTokenByName} from "~/utilities/resolveDesignTokenByName";

/** Product-wide calendar accent palette (maps to Atlaskit design tokens). */
export type CalendarAccentColor = "gray" | "blue" | "purple" | "green" | "red" | "yellow";

const CALENDAR_ACCENT_COLOR_TOKEN_CONFIG: Record<CalendarAccentColor, SharedCalendarColorConfig> = {
    gray: {
        backgroundColorToken: "color.background.accent.gray.subtler",
        borderColorToken: "color.border.accent.gray",
        textColorToken: "color.text.accent.gray",
    },
    blue: {
        backgroundColorToken: "color.background.accent.blue.subtlest",
        borderColorToken: "color.border.accent.blue",
        textColorToken: "color.text.accent.blue",
    },
    purple: {
        backgroundColorToken: "color.background.accent.purple.subtlest",
        borderColorToken: "color.border.accent.purple",
        textColorToken: "color.text.accent.purple",
    },
    green: {
        backgroundColorToken: "color.background.accent.green.subtlest",
        borderColorToken: "color.border.accent.green",
        textColorToken: "color.text.accent.green",
    },
    red: {
        backgroundColorToken: "color.background.accent.red.subtlest",
        borderColorToken: "color.border.accent.red",
        textColorToken: "color.text.accent.red",
    },
    yellow: {
        backgroundColorToken: "color.background.accent.yellow.subtlest",
        borderColorToken: "color.border.accent.yellow",
        textColorToken: "color.text.accent.yellow",
    },
};

export function calendarAccentToSharedColorConfig(accent: CalendarAccentColor): SharedCalendarColorConfig {
    return CALENDAR_ACCENT_COLOR_TOKEN_CONFIG[accent];
}

const CALENDAR_ACCENT_KANBAN_COLUMN_COLORS: Record<
    CalendarAccentColor,
    {background: string; textColor: string; borderColor: string}
> = {
    gray: {
        background: token("color.background.accent.gray.subtler"),
        textColor: token("color.text.accent.gray"),
        borderColor: token("color.border.accent.gray"),
    },
    blue: {
        background: token("color.background.accent.blue.subtlest"),
        textColor: token("color.text.accent.blue"),
        borderColor: token("color.border.accent.blue"),
    },
    purple: {
        background: token("color.background.accent.purple.subtlest"),
        textColor: token("color.text.accent.purple"),
        borderColor: token("color.border.accent.purple"),
    },
    green: {
        background: token("color.background.accent.green.subtlest"),
        textColor: token("color.text.accent.green"),
        borderColor: token("color.border.accent.green"),
    },
    red: {
        background: token("color.background.accent.red.subtlest"),
        textColor: token("color.text.accent.red"),
        borderColor: token("color.border.accent.red"),
    },
    yellow: {
        background: token("color.background.accent.yellow.subtlest"),
        textColor: token("color.text.accent.yellow"),
        borderColor: token("color.border.accent.yellow"),
    },
};

/** Kanban column header + list lozenge colors derived from the same accent palette as calendar cards. */
export function calendarAccentToKanbanColumnColors(accent: CalendarAccentColor): {background: string; textColor: string; borderColor: string} {
    return CALENDAR_ACCENT_KANBAN_COLUMN_COLORS[accent];
}

export function normalizeCalendarStatusLookupKey(value: string): string {
    return value.trim().toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
}

export function sharedCalendarColorConfigToEventColorMap(colorConfig: SharedCalendarColorConfig): EventColorMap {
    return {
        backgroundColor:
            colorConfig.backgroundColorToken !== undefined
                ? resolveDesignTokenByName(colorConfig.backgroundColorToken)
                : undefined,
        borderColor:
            colorConfig.borderColorToken !== undefined ? resolveDesignTokenByName(colorConfig.borderColorToken) : undefined,
        textColor:
            colorConfig.textColorToken !== undefined ? resolveDesignTokenByName(colorConfig.textColorToken) : undefined,
    };
}

export function calendarEventColorMapHasColors(colorMap: EventColorMap): boolean {
    return (
        colorMap.backgroundColor !== undefined ||
        colorMap.borderColor !== undefined ||
        colorMap.textColor !== undefined
    );
}

export function buildCalendarEventTypeColorMapFromAccents(
    entries: Array<{keys: Array<string>; accent: CalendarAccentColor}>,
): Record<string, SharedCalendarColorConfig> {
    const map: Record<string, SharedCalendarColorConfig> = {};
    for (const entry of entries) {
        const colorConfig = calendarAccentToSharedColorConfig(entry.accent);
        for (const key of entry.keys) {
            map[key] = colorConfig;
            map[normalizeCalendarStatusLookupKey(key)] = colorConfig;
        }
    }
    return map;
}

export function resolveCalendarEventTypeColorFromTokenConfigMap(
    type: string | undefined,
    colorMap: Record<string, SharedCalendarColorConfig>,
): EventColorMap {
    if (type === undefined) {
        return {};
    }
    let colorConfig = colorMap[type] ?? colorMap[normalizeCalendarStatusLookupKey(type)];
    if (colorConfig === undefined) {
        const normalizedType = type.trim().toLowerCase();
        for (const [key, entry] of Object.entries(colorMap)) {
            if (key.toLowerCase() === normalizedType) {
                colorConfig = entry;
                break;
            }
        }
    }
    if (colorConfig === undefined) {
        return {};
    }
    return sharedCalendarColorConfigToEventColorMap(colorConfig);
}

export function createCalendarGetEventColorFromTokenConfigMap(
    colorMap: Record<string, SharedCalendarColorConfig>,
    fallback?: (type: string | undefined) => EventColorMap,
): (type: string | undefined) => EventColorMap {
    return (type: string | undefined) => {
        const resolved = resolveCalendarEventTypeColorFromTokenConfigMap(type, colorMap);
        if (calendarEventColorMapHasColors(resolved) === true) {
            return resolved;
        }
        return fallback?.(type) ?? {};
    };
}

export const DEFAULT_CALENDAR_SCHEDULE_EMPTY_STATE: ResolvedCalendarScheduleEmptyState = {
    createButtonLabel: "Create event",
    title: "No events scheduled yet",
    titleWithFilters: "No events match your filters",
    description: "Nothing is scheduled for this period yet. Create your first item to see it on the calendar.",
    descriptionWithFilters: "Try adjusting your filters to see more items, or create a new one.",
};

/** Emotorad leads template - lead-specific titles and copy. */
export const EMOTORAD_LEADS_CALENDAR_SCHEDULE_EMPTY_STATE: ResolvedCalendarScheduleEmptyState = {
    createButtonLabel: "Create lead",
    title: "No leads scheduled yet",
    titleWithFilters: "No leads match your filters",
    description: "Create your first lead to start tracking scheduled follow-ups on the calendar.",
    descriptionWithFilters: "Try adjusting your filters to see more leads, or create a new lead.",
    createFormId: EMOTORAD__LEAD__CREATE__FORM__ID,
};

/** Other Emotorad event calendars - generic copy with create-lead action. */
export const EMOTORAD_CALENDAR_SCHEDULE_EMPTY_STATE: ResolvedCalendarScheduleEmptyState = {
    ...DEFAULT_CALENDAR_SCHEDULE_EMPTY_STATE,
    createButtonLabel: "Create lead",
    createFormId: EMOTORAD__LEAD__CREATE__FORM__ID,
};

export const EMOTORAD_SALES_CALENDAR_SCHEDULE_EMPTY_STATE: ResolvedCalendarScheduleEmptyState = {
    ...DEFAULT_CALENDAR_SCHEDULE_EMPTY_STATE,
    createButtonLabel: "Create sale",
    createFormId: EMOTORAD__SALES__SCHEDULE__FORM__ID,
};

export function resolveCalendarLegendItems(
    config: SharedCalendarViewConfig,
    fallbackConfig: SharedCalendarViewConfig,
): Array<SharedCalendarLegendItem> {
    return config.legendItems ?? fallbackConfig.legendItems ?? [];
}

export function resolveCalendarEventTypeLabel(
    type: string,
    config: SharedCalendarViewConfig,
    fallbackConfig: SharedCalendarViewConfig,
    unknownLabel: string,
): string {
    const labelMap = config.eventTypeLabelMap ?? fallbackConfig.eventTypeLabelMap;
    if (labelMap === undefined) {
        return unknownLabel;
    }
    const direct = labelMap[type];
    if (direct !== undefined) {
        return direct;
    }
    const normalizedType = type.trim().toLowerCase();
    if (normalizedType === "") {
        return unknownLabel;
    }
    for (const [key, label] of Object.entries(labelMap)) {
        if (key.toLowerCase() === normalizedType) {
            return label;
        }
    }
    return unknownLabel;
}

/** Matches archived list/kanban lozenge styling (`listStatusPillTypeToLozengeStyle` -> Archived). */
export function getArchivedModuleCalendarEventColors(): EventColorMap {
    return {
        backgroundColor: token("color.background.accent.red.subtlest"),
        borderColor: token("color.background.accent.red.subtlest"),
        textColor: token("color.text.danger"),
    };
}

export const ARCHIVED_MODULE_CALENDAR_ALL_DAY_STYLE = {mode: "inheritStatus"} as const;

export function wrapGetEventColorForArchivedModuleSlice(
    getEventColor: (type: string | undefined) => EventColorMap,
    isArchivedSlice: boolean,
): (type: string | undefined) => EventColorMap {
    if (isArchivedSlice !== true) {
        return getEventColor;
    }
    const archivedColors = getArchivedModuleCalendarEventColors();
    return () => archivedColors;
}

export function resolveCalendarAllDayStyleForModuleSlice(
    allDayStyle: {mode: "fixedGreen" | "inheritStatus"},
    isArchivedSlice: boolean,
): {mode: "fixedGreen" | "inheritStatus"} {
    return isArchivedSlice === true ? ARCHIVED_MODULE_CALENDAR_ALL_DAY_STYLE : allDayStyle;
}

export function resolveCalendarEventTypeColor(
    type: string | undefined,
    config: SharedCalendarViewConfig,
    fallbackConfig: SharedCalendarViewConfig,
): EventColorMap {
    if (type === undefined) {
        return {};
    }
    const colorMap = config.eventTypeColorMap ?? fallbackConfig.eventTypeColorMap ?? {};
    let colorConfig = colorMap[type] ?? colorMap.deleted;
    if (colorConfig === undefined) {
        const normalizedType = type.trim().toLowerCase();
        for (const [key, entry] of Object.entries(colorMap)) {
            if (key.toLowerCase() === normalizedType) {
                colorConfig = entry;
                break;
            }
        }
    }
    if (colorConfig === undefined) {
        return {};
    }
    return sharedCalendarColorConfigToEventColorMap(colorConfig);
}

export function resolveCalendarTemplateStrokeColor(
    templateId: string,
    config: SharedCalendarViewConfig,
    fallbackConfig: SharedCalendarViewConfig,
): string {
    const tokenName = config.templateStrokeColorMap?.[templateId] ?? fallbackConfig.templateStrokeColorMap?.[templateId];
    return tokenName !== undefined
        ? resolveDesignTokenByName(tokenName)
        : resolveDesignTokenByName("color.border");
}

export function resolveCalendarAllDayStyle(
    config: SharedCalendarViewConfig,
    fallbackConfig: SharedCalendarViewConfig,
): {mode: "fixedGreen" | "inheritStatus"} {
    return config.allDayStyle ?? fallbackConfig.allDayStyle ?? {mode: "fixedGreen"};
}

export function resolveCalendarScheduleEmptyState(
    config: SharedCalendarViewConfig | undefined,
    fallback: ResolvedCalendarScheduleEmptyState,
): ResolvedCalendarScheduleEmptyState {
    const emptyState: SharedCalendarScheduleEmptyStateConfig | undefined = config?.scheduleEmptyState;
    return {
        createButtonLabel: emptyState?.createButtonLabel ?? fallback.createButtonLabel,
        title: emptyState?.title ?? fallback.title,
        titleWithFilters: emptyState?.titleWithFilters ?? fallback.titleWithFilters,
        description: emptyState?.description ?? fallback.description,
        descriptionWithFilters: emptyState?.descriptionWithFilters ?? fallback.descriptionWithFilters,
        createFormId: emptyState?.createFormId ?? fallback.createFormId,
    };
}
